import { Box, Button, SvgIcon } from '@mui/material';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Next from 'assets/icons/next.svg';
import deferRender from 'defer-render-hoc';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';
import SwipeableViews from 'react-swipeable-views';
import companyActions from 'store/app/entities/companies/action';
import authCompany from 'store/selectors/authCompany';
import authEmployee from 'store/selectors/authEmployee';
import isSuperAdminSelector from 'store/selectors/isSuperAdmin';
import { I18n } from 'utils/i18n';
import useActions from 'utils/useActions';
import CSSStyledStepper from './CSSStyledStepper';
import img0 from './images/onboarding-mock-0.png';
import img1 from './images/onboarding-mock-1.png';
import img2 from './images/onboarding-mock-2.png';
import img3 from './images/onboarding-mock-3.png';

const ONBOARDING_STEPS = Array.from({ length: 4 }, (_, index) => index);
const STEP_DURATION = 5000;

const paperSx = {
  width: '580px',
  maxWidth: '100%',
  maxHeight: '720px',
  margin: '1em 0',
  display: 'flex',
  flexDirection: 'column',
  boxShadow:
    '0px 11px 15px -7px rgba(0,0,0,0.05), ' +
    '0px 24px 38px 3px rgba(0,0,0,0.02), ' +
    '0px 9px 46px 8px rgba(0,0,0,0.01)',
  flexGrow: 1,
  overflow: 'hidden',
};

const ImgSwipeableViews = styled(SwipeableViews)({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '1.75em',
});

const slideImageBaseSx = {
  flexGrow: 1,
  minHeight: '200px',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

const stepImageSx = {
  0: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.0) 70%, rgba(0, 0, 0, 0.4)), url(${img0})`,
  },
  1: {
    backgroundImage: `url(${img1})`,
  },
  2: {
    backgroundImage: `url(${img2})`,
  },
  3: {
    backgroundImage: `url(${img3})`,
  },
};

const OnboardingScene = () => {
  const [activeStep, setActiveStep] = useState(0);
  const company = useSelector(authCompany);
  const employee = useSelector(authEmployee);
  const isSuperAdmin = useSelector(isSuperAdminSelector);
  const loadOnboarding = useActions(companyActions.loadOnboarding);

  useEffect(() => {
    if (!company?.onBoardingImage) {
      loadOnboarding();
    }
  }, [company, loadOnboarding]);

  const handleStepChange = useCallback(step => {
    setActiveStep(step);
  }, []);

  const employeeName = useMemo(
    () => (isSuperAdmin ? 'Super Admin' : employee?.name || ''),
    [employee, isSuperAdmin],
  );

  return (
    <Box
      component="section"
      sx={{
        minHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <Paper sx={paperSx} elevation={24}>
        <ImgSwipeableViews
          axis="x"
          index={activeStep}
          onChangeIndex={handleStepChange}
          enableMouseEvents
          containerStyle={{
            flexGrow: 1,
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            alignContent: 'stretch',
          }}
          slideStyle={{ display: 'flex' }}
        >
          {ONBOARDING_STEPS.map(step => (
            <Box
              key={step}
              sx={{
                ...slideImageBaseSx,
                ...stepImageSx[step],
                ...(step === 0 &&
                  company.onBoardingImage && {
                    backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.0) 70%, rgba(0, 0, 0, 0.4)), url(${company.onBoardingImage})`,
                  }),
              }}
            >
              {step === 0 && company.logo ? (
                <Box
                  component="img"
                  src={company.logo}
                  alt={company.name}
                  sx={{
                    maxHeight: '60px',
                    maxWidth: '200px',
                    textAlign: 'center',
                    marginBottom: '20px',
                  }}
                />
              ) : null}
            </Box>
          ))}
        </ImgSwipeableViews>
        <CSSStyledStepper
          steps={ONBOARDING_STEPS.length}
          stepDuration={STEP_DURATION}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
        />
        <SwipeableViews
          axis="x"
          index={activeStep}
          onChangeIndex={handleStepChange}
          enableMouseEvents
        >
          {ONBOARDING_STEPS.map(step => (
            <div key={step}>
              <Box
                component="section"
                sx={{
                  textAlign: 'center',
                  padding: '0 3em',
                }}
              >
                <Typography sx={{ margin: '1em 0 0.5em 0' }} gutterBottom variant="h5">
                  {I18n.t(`Onboarding_Screen.Slide_${step + 1}_Title`, {
                    name: employeeName,
                  }).replace(' !', '!')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 'normal',
                    marginBottom: '2em',
                  }}
                >
                  {I18n.t(`Onboarding_Screen.Slide_${step + 1}_Description`, {
                    name: company.name,
                  })}
                </Typography>
              </Box>
            </div>
          ))}
        </SwipeableViews>
        <Box
          component={Link}
          to="/welcome/legal"
          sx={{
            placeSelf: 'center',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            sx={{
              padding: '0 3em',
              marginBottom: '4em',
              '& *': {
                verticalAlign: 'center',
              },
              '& :first-child': {
                marginRight: '0.25em',
              },
            }}
          >
            <span>{I18n.t('Onboarding_Screen.Enter')}</span>
            <SvgIcon sx={{ width: '16px', height: '16px' }}>
              <Next />
            </SvgIcon>
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default deferRender(OnboardingScene);
